import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/forms/Checkbox';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { SchedulingProfile } from '../types';
import { useProfileSave } from '../useProfileSave';

const GRANULARITIES = [5, 10, 15, 20, 30, 60];

export const SettingsTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t } = useTranslation();
  const { save, isSaving, fieldErrors, formError } = useProfileSave(profile.id);

  const [name, setName] = useState(profile.name);
  const [isActive, setIsActive] = useState(profile.is_active);
  const [granularity, setGranularity] = useState(String(profile.slot_granularity_minutes));
  const [leadTime, setLeadTime] = useState(String(profile.min_lead_time_minutes));
  const [maxAdvance, setMaxAdvance] = useState(String(profile.max_advance_days));

  const granularityOptions = GRANULARITIES.map(minutes => ({
    value: String(minutes),
    label: t('schedulingAdmin.settings.minutes', { count: minutes }),
  }));

  const handleSave = () =>
    save({
      name: name.trim(),
      is_active: isActive,
      slot_granularity_minutes: Number(granularity),
      min_lead_time_minutes: Number(leadTime),
      max_advance_days: Number(maxAdvance),
    });

  return (
    <div className="max-w-2xl space-y-6">
      <Input
        label={t('schedulingAdmin.fields.name')}
        value={name}
        onChange={e => setName(e.target.value)}
        errorText={fieldErrors.name}
        state={fieldErrors.name ? 'error' : 'default'}
        fullWidth
        required
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('schedulingAdmin.fields.timezone')}
        </label>
        <p className="text-sm text-gray-900 dark:text-gray-100">{profile.timezone}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.settings.timezoneFromOrganization')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('schedulingAdmin.settings.granularity')}
        </label>
        <Select
          options={granularityOptions}
          value={granularity}
          onChange={value => setGranularity(value as string)}
          helperText={t('schedulingAdmin.settings.granularityHelp')}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          type="number"
          min={0}
          label={t('schedulingAdmin.settings.leadTime')}
          value={leadTime}
          onChange={e => setLeadTime(e.target.value)}
          helperText={t('schedulingAdmin.settings.leadTimeHelp')}
          errorText={fieldErrors.min_lead_time_minutes}
          fullWidth
        />
        <Input
          type="number"
          min={1}
          label={t('schedulingAdmin.settings.maxAdvance')}
          value={maxAdvance}
          onChange={e => setMaxAdvance(e.target.value)}
          helperText={t('schedulingAdmin.settings.maxAdvanceHelp')}
          errorText={fieldErrors.max_advance_days}
          fullWidth
        />
      </div>

      <Checkbox
        label={t('schedulingAdmin.settings.active')}
        helperText={t('schedulingAdmin.settings.activeHelp')}
        checked={isActive}
        onChange={e => setIsActive(e.target.checked)}
      />

      {formError && <p className="text-sm text-red-600">{formError}</p>}
      <Button onClick={handleSave} loading={isSaving} disabled={!name.trim()}>
        {t('common.save')}
      </Button>
    </div>
  );
};
